import { Injectable } from '@angular/core';
import {
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  RedirectCommand,
} from '@angular/router';
import { DatabaseService } from '../db/database.service';
import { AuthenticationService } from './authentication.service';
import { isRxDatabase } from 'rxdb';
import {
  auth,
  exchangePasswordResetToken,
  refreshSessionSingleFlight,
} from './nhost';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import {
  isHostCompatibleWithBuild,
  isLocaleAllowedOnCurrentFront,
} from '../utils/brand-context';

const WRONG_BRAND_ERROR = 'wrong-brand';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  hasCheckedUpdate: boolean = false;
  platform = Capacitor.getPlatform();

  constructor(
    public router: Router,
    public auth: AuthenticationService,
    public db: DatabaseService
  ) {}

  /**
   * Permet de vérifier le droit d'accès a une page
   * Vérification effectuées :
   * - Vérification de la version avec le LIVEUPDATE (Une seule fois)
   * - Vérification de l'authentification de l'utilisateur
   * - Vérification de l'existance de la base de données IDB
   * @param {ActivatedRouteSnapshot} _next -
   * @param {RouterStateSnapshot} state - 
   * @return {| Observable<boolean | UrlTree>
      | Promise<boolean | UrlTree>
      | boolean
      | UrlTree} 
   */
  public async canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree | RedirectCommand> {
    const queryParams = next.queryParams;

    if (this.handleTicketVerification(queryParams)) {
      // Si la vérification du ticket entraîne une redirection, on termine l'exécution ici.
      return false;
    }

    const isPasswordResetCallback = this.isPasswordResetCallback(queryParams);

    if (
      !isPasswordResetCallback &&
      this.needToUpdate(state.url, queryParams)
    ) {
      // console.log('AUTH Redirect to updating');
      return this.redirectTo(['/updating'], state.url, queryParams);
    }

    if (!isHostCompatibleWithBuild()) {
      await this.auth.clearAuthState();
      if (isPasswordResetCallback) {
        return this.redirectPasswordReset(['/login'], {
          error: WRONG_BRAND_ERROR,
        });
      }
      return this.redirectTo(['/login'], state.url, {
        ...queryParams,
        error: WRONG_BRAND_ERROR,
      });
    }

    // 👇👇 NOUVEAU BLOC : gestion du mode offline mobile
    const isMobile = this.platform === 'android' || this.platform === 'ios';
    // Utiliser Capacitor Network sur mobile pour plus de fiabilité
    const { connected } = isMobile ? await Network.getStatus() : { connected: navigator.onLine };
    const isOnline = connected;

    if (!isOnline && isMobile) {
      // On est sur mobile, sans réseau
      const hasLocalSession = this.hasLocalSession();
      const dbReady = this.isDatabaseInitialized();

      // Un callback de réinitialisation doit échanger son propre token en ligne.
      // Une session locale ne doit pas autoriser le reset d'un autre compte.
      if (
        hasLocalSession &&
        dbReady &&
        !this.isPasswordResetCallback(queryParams)
      ) {
        if (!(await this.isAllowedOnCurrentFront())) {
          return this.redirectTo(['/login'], state.url, {
            ...queryParams,
            error: WRONG_BRAND_ERROR,
          });
        }

        // 🔓 Mode "offline-authenticated" :
        // - Nhost a encore une session en local
        // - RxDB prête
        // → on autorise l'accès SANS tenter de refresh ni d'appeler Nhost
        return true;
      }

      // Sans session locale prête, ou pour valider un reset en ligne,
      // poursuivre le flux normal.
    }

    if (!(await this.isAuthenticated(queryParams))) {
      // console.log('AUTH Redirect to login');
      if (isPasswordResetCallback) {
        return this.redirectPasswordReset(['/login'], {
          error: 'invalid-ticket',
          returnUrl: '/',
        });
      }
      return this.redirectTo(['/login'], state.url, queryParams);
    }

    // A reset session is intentionally isolated from the shared app session.
    // Route it to a minimal page before locale/database gates that require the
    // normal authenticated application context.
    if (isPasswordResetCallback) {
      return this.redirectPasswordReset(['/reset-password']);
    }

    if (!(await this.isAllowedOnCurrentFront())) {
      return this.redirectTo(['/login'], state.url, {
        ...queryParams,
        error: WRONG_BRAND_ERROR,
      });
    }

    if (!this.isDatabaseInitialized()) {
      // console.log('AUTH Redirect to loading');
      return this.redirectTo(['/loading'], state.url, queryParams);
    }

    return true;
  }

  private handleTicketVerification(queryParams: any): boolean {
    const { ticket, redirectTo, type } = queryParams;
    if (ticket && redirectTo && type) {
      const authUrl = auth.url; // Assurez-vous que cette méthode existe et retourne l'URL appropriée
      window.location.href = `${authUrl}/verify?ticket=${ticket}&type=${type}&redirectTo=${redirectTo}`;
      return true;
    }
    return false;
  }

  private needToUpdate(requestedUrl: string, queryParams: any): boolean {
    if (
      (this.platform === 'android' || this.platform === 'ios') &&
      !this.hasCheckedUpdate &&
      !queryParams.skipUpdate
    ) {
      this.hasCheckedUpdate = true;
      return true;
    }
    return false;
  }

  private async isAuthenticated(queryParams: any): Promise<boolean> {
    const isPasswordResetCallback = this.isPasswordResetCallback(queryParams);
    if (isPasswordResetCallback) {
      if (
        typeof queryParams.refreshToken !== 'string' ||
        queryParams.refreshToken.length === 0
      ) {
        return false;
      }
      const { session, error } = await exchangePasswordResetToken(
        queryParams.refreshToken
      );
      return !!session && !error;
    }
    const isLoggedIn = await this.auth.isLoggedInAsync();
    if (!isLoggedIn && this.canRefreshSession(queryParams)) {
      const { error } = await refreshSessionSingleFlight(queryParams.refreshToken);
      return !error;
    }
    return isLoggedIn;
  }

  private isPasswordResetCallback(queryParams: any): boolean {
    return queryParams.type === 'passwordReset';
  }

  private canRefreshSession(queryParams: any): boolean {
    const type = queryParams.type;
    const splitedType = type ? type.split(':') : [];

    // NEW WAY (ex: signinPasswordless:fa001f9d-bfb5-409e-b0c6-a569e763e787)
    if (splitedType.length === 2 && type.startsWith('signinPasswordless')) {
      return true;
    }
    if (splitedType.length === 2 && type.startsWith('passwordlessEmail')) {
      return true;
    }
    if (splitedType.length === 2 && type.startsWith('emailVerify')) {
      return true;
    }
    if (splitedType.length === 2 && type.startsWith('verifyEmail')) {
      return true;
    }

    // OLD WAY (refreshToken as query param)
    return (
      queryParams.refreshToken &&
      [
        'signinPasswordless',
        'emailVerify',
        'verifyEmail',
        'passwordlessEmail',
      ].includes(queryParams.type)
    );
  }

  private async isAllowedOnCurrentFront(): Promise<boolean> {
    const userLocale = this.auth.getUserLocale();
    if (!userLocale) {
      return true;
    }

    if (isLocaleAllowedOnCurrentFront(userLocale)) {
      return true;
    }

    await this.auth.clearAuthState();
    return false;
  }

  private isDatabaseInitialized(): boolean {
    return isRxDatabase(this.db.dbInstance);
  }

  private redirectTo(
    route: any[],
    requestedUrl: string,
    queryParams: any
  ): UrlTree {
    return this.router.createUrlTree(route, {
      queryParams: { ...queryParams, returnUrl: requestedUrl },
    });
  }

  private redirectPasswordReset(
    route: string[],
    queryParams?: Record<string, string>
  ): RedirectCommand {
    const urlTree = queryParams
      ? this.router.createUrlTree(route, { queryParams })
      : this.router.createUrlTree(route);
    return new RedirectCommand(urlTree, { replaceUrl: true });
  }

  /**
   * Vérifie si Nhost a déjà une session en local
   * (user en cache localStorage / sessionStorage, sans call réseau).
   */
  hasLocalSession(): boolean {
    const user = auth.getUser?.(); // selon ton wrapper; avec nhost-js v4 c'est auth.auth.getUser()
    return !!user;
  }
}
